/**
 * Signup Page
 * User registration with email/password
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { useAuth, useRegister } from '@/hooks/use-auth';
import { toast } from '@/stores/ui-store';
import { Mail, Lock, User, ArrowLeft, CheckCircle } from 'lucide-react';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  
  const { isAuthenticated, isInitialized } = useAuth();
  const registerMutation = useRegister();
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      const redirect = returnUrl ? decodeURIComponent(returnUrl) : '/';
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, returnUrl]);
  
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle signup
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    
    try {
      await registerMutation.mutateAsync({
        email: email.trim(),
        password,
        name: fullName,
      });
      
      setShowSuccess(true);
      toast.success('Account created successfully', 'Please check your email to verify your account');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      toast.error('Signup failed', message);
    }
  };
  
  // Loading state
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }
  
  // Success state
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-[#e5e5e5]">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-lg bg-[#000000] flex items-center justify-center">
                <span className="text-[#ffffff] font-bold text-xl">W</span>
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center text-[#000000]">
              Account Created
            </CardTitle>
            <CardDescription className="text-center text-[#666666]">
              Welcome to WillScraper
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-[#000000] flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-[#ffffff]" />
              </div>
              <p className="text-[#333333] mb-2">
                Your account has been created successfully.
              </p>
              <p className="text-[#666666] text-sm mb-6">
                We've sent a verification email to:<br />
                <span className="font-medium text-[#000000]">{email}</span>
              </p>
              <p className="text-[#666666] text-sm mb-6">
                Please check your email and click the verification link to activate your account.
              </p>
              <Button
                className="w-full bg-[#000000] hover:bg-[#1a1a1a]"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main signup form
  return (
    <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#e5e5e5]">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 -ml-2"
              onClick={() => navigate('/login')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 rounded-lg bg-[#000000] flex items-center justify-center">
              <span className="text-[#ffffff] font-bold text-xl">W</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-[#000000]">
            Create Account
          </CardTitle>
          <CardDescription className="text-center text-[#666666]">
            Sign up to get started with WillScraper
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-[#333333]">
                  First Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) {
                        setErrors(prev => ({ ...prev, firstName: '' }));
                      }
                    }}
                    className={`pl-10 ${errors.firstName ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.firstName && (
                  <p className="text-xs text-red-500">{errors.firstName}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-[#333333]">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName) {
                      setErrors(prev => ({ ...prev, lastName: '' }));
                    }
                  }}
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>
            
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#333333]">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: '' }));
                    }
                  }}
                  className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#333333]">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors(prev => ({ ...prev, password: '' }));
                    }
                  }}
                  className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password}</p>
              )}
              <p className="text-xs text-[#999999]">
                Must be at least 8 characters
              </p>
            </div>
            
            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#333333]">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) {
                      setErrors(prev => ({ ...prev, confirmPassword: '' }));
                    }
                  }}
                  className={`pl-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-500">{errors.confirmPassword}</p>
              )}
            </div>
            
            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-[#000000] hover:bg-[#1a1a1a]"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                'Create Account'
              )}
            </Button>
            
            {/* Login Link */}
            <div className="text-center pt-2">
              <p className="text-sm text-[#666666]">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#000000] hover:underline font-medium"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
